import React, { useState, useEffect, useContext } from "react";
import { userInitState, userReducer } from "../reducer";
import userApi from "@api/user";
import { getCachedData } from "@core/utils/localStorageManager";
import { USER } from "@features/User/actions";
import ohlcvApi from "@api/ohlcv";
import { StatsContext } from "../../../context/statsContext";
import { UpdateSettingContext } from "@context/UpdateSettingProvider";
import { statsTime } from "@core/utils/IntervalApiTime";
import { useRangerOptional } from "@context/rangerContext";

const UserDispatchContext = React.createContext();
const UserStateContext = React.createContext();

function UserProvider({ children }) {
  const UpdateSetting = useContext(UpdateSettingContext);
  const ranger = useRangerOptional();
  const initialToken = getCachedData("access_token");
  const initState = {
    ...userInitState,
    token: initialToken,
    authorized: Boolean(initialToken),
    isLoading: Boolean(initialToken),
  };

  const { statsDispatch } = useContext(StatsContext);
  const [state, dispatch] = React.useReducer(userReducer, initState);

  useEffect(() => {
    if (!state.token) {
      return;
    }

    userApi
      .getCurrentUserSummary()
      .then(({ content }) => {
        dispatch({
          type: USER.FETCH.SUCCESS,
          payload: { user: content },
        });
      })
      .catch(() => {
        dispatch({ type: USER.SESSION_EXPIRED });
      });
  }, [state.token, UpdateSetting.updateUser]);

  // Fallback polling when Rango WS is disabled or disconnected (WS5.4).
  useEffect(() => {
    if (!state.authorized) {
      return;
    }
    if (ranger?.enabled && ranger?.connected) {
      return;
    }

    let timerToClear = null;
    (function fetch() {
      ohlcvApi
        .getStats()
        .then((response) => {
          statsDispatch({
            type: "SET_STATS",
            stats: response.content.statMap,
          });
        })
        .catch((error) => console.log(error));

      timerToClear = setTimeout(fetch, statsTime * 1000);
    })();

    return () => clearTimeout(timerToClear);
  }, [state.authorized, statsDispatch, ranger?.enabled, ranger?.connected]);

  // WS4 — private ranger via BFF proxy after login (stable deps — avoid reconnect thrash)
  const connectPrivateRef = React.useRef(ranger?.connectPrivate);
  const disconnectPrivateRef = React.useRef(ranger?.disconnectPrivate);
  connectPrivateRef.current = ranger?.connectPrivate;
  disconnectPrivateRef.current = ranger?.disconnectPrivate;

  useEffect(() => {
    if (!ranger?.enabled) return undefined;
    if (state.authorized) {
      connectPrivateRef.current?.();
      return () => disconnectPrivateRef.current?.();
    }
    disconnectPrivateRef.current?.();
    return undefined;
  }, [state.authorized, ranger?.enabled]);

  return (
    <UserStateContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        {children}
      </UserDispatchContext.Provider>
    </UserStateContext.Provider>
  );
}

function useUserState() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUserState must be used within a UserProvider");
  }
  return context;
}

function useUserDispatch() {
  const context = React.useContext(UserDispatchContext);
  if (context === undefined) {
    throw new Error("useUserDispatch must be used within a UserProvider");
  }
  return context;
}

export { UserProvider, useUserState, useUserDispatch };
